<template>
  <Panel lineColor="var(--primary-color)" class="rfm-panel" :id="cardId">
    <div class="section-header">
      <div>
        <h3 class="section-title">{{ titulo }}</h3>
        <p class="section-subtitle">{{ descricao }}</p>
      </div>
      <FormButton
        outlined
        type="primario"
        color="primario"
        text="Restaurar padrão"
        :id="`btn-restaurar-${dimensao}`"
        @click="$emit('restaurar')"
      />
    </div>

    <p class="nota-informativa">
      <v-icon size="14" color="primario">fas fa-info-circle</v-icon>
      <strong>Preenchimento automático:</strong> informe apenas a coluna
      <strong>{{ rotuloAte }}</strong
      >. A coluna <strong>{{ rotuloDe }}</strong> é calculada a partir da faixa
      vizinha.
    </p>

    <v-data-table
      :headers="headers"
      :items="faixas"
      item-key="score"
      hide-default-footer
      disable-pagination
      disable-sort
      class="rfm-table"
    >
      <template v-slot:item="{ item }">
        <tr :class="{ 'linha-com-erro': temErro(item.score) }">
          <td class="text-center">
            <Chip
              :color="corDaNota(item.score)"
              :text="String(item.score)"
              small
            />
          </td>
          <td>
            <div>{{ classificacoes[item.score] }}</div>
            <div v-if="temErro(item.score)" class="rfm-erro-linha">
              {{ primeiroErro(item.score) }}
            </div>
          </td>
          <td class="rfm-valor-automatico">{{ formatar(item.de) }}</td>
          <td>
            <span v-if="item.semLimite" class="rfm-sem-limite">Sem limite</span>
            <FormField
              v-else
              class="rfm-input-faixa"
              :value="item.ate"
              typeField="number"
              :step="step"
              minField="0"
              :placeholder="placeholder"
              :id="`input-${dimensao}-ate-${item.score}`"
              @input="valor => $emit('alterar', { score: item.score, valor })"
            />
          </td>
          <td>
            <strong>{{ regraFormatador(item) }}</strong>
          </td>
        </tr>
      </template>
    </v-data-table>

    <div v-if="erros.length" class="nota-informativa nota-erro">
      <v-icon size="14" color="error">fas fa-exclamation-circle</v-icon>
      <strong>Revise os intervalos de {{ titulo }}:</strong>
      <ul>
        <li v-for="(err, i) in erros" :key="`${dimensao}-erro-${i}`">
          {{ err.mensagem }}
        </li>
      </ul>
    </div>
  </Panel>
</template>

<script>
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import FormField from "@/components/form/FormField";
import Chip from "@/components/Chip";

const CORES_POR_NOTA = {
  5: "green",
  4: "cyan",
  3: "primario",
  2: "orange",
  1: "red"
};

export default {
  name: "MatrizRfmFaixasTable",
  components: { Panel, FormButton, FormField, Chip },
  props: {
    dimensao: { type: String, required: true },
    titulo: { type: String, required: true },
    descricao: { type: String, required: true },
    faixas: { type: Array, required: true },
    erros: { type: Array, default: () => [] },
    classificacoes: { type: Object, required: true },
    rotuloDe: { type: String, required: true },
    rotuloAte: { type: String, required: true },
    step: { type: String, default: "1" },
    placeholder: { type: String, default: "" },
    /** Formata De/Até para exibição (dias, pedidos ou moeda). */
    formatador: { type: Function, default: valor => valor },
    /** Monta o texto da coluna "Regra resultante" da faixa. */
    regraFormatador: { type: Function, required: true },
    cardId: { type: String, default: "" }
  },
  computed: {
    headers() {
      return [
        { text: "Pontuação", value: "score", align: "center" },
        { text: "Classificação", value: "classificacao" },
        { text: `${this.rotuloDe} (automático)`, value: "de" },
        { text: this.rotuloAte, value: "ate" },
        { text: "Regra resultante", value: "regra" }
      ];
    }
  },
  methods: {
    corDaNota(score) {
      return CORES_POR_NOTA[score] || "grey";
    },
    errosDaFaixa(score) {
      return this.erros.filter(err => err.score === score);
    },
    temErro(score) {
      return this.errosDaFaixa(score).length > 0;
    },
    primeiroErro(score) {
      const encontrado = this.errosDaFaixa(score)[0];
      return encontrado ? encontrado.mensagem : "";
    },
    formatar(valor) {
      if (valor === null || valor === undefined || valor === "") return "-";
      return this.formatador(valor);
    }
  }
};
</script>

<style scoped>
.rfm-panel {
  margin-bottom: 20px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title,
.section-subtitle,
.nota-informativa {
  text-align: left;
}

.nota-informativa .v-icon {
  margin-right: 8px;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.section-subtitle {
  color: #6a6c6f;
  font-size: 16px;
  margin-bottom: 16px;
}

.nota-informativa {
  background: #f5f7fa;
  border-left: 3px solid var(--primary-color);
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 16px;
  color: #6a6c6f;
}

.nota-erro {
  border-left-color: var(--danger-color);
  margin-top: 16px;
  margin-bottom: 0;
}

.nota-erro ul {
  margin: 4px 0 0;
}

.rfm-table {
  margin-top: 8px;
}

.linha-com-erro {
  background-color: rgba(255, 82, 82, 0.07);
}

.rfm-erro-linha {
  color: var(--danger-color);
  font-size: 12px;
}

.rfm-valor-automatico {
  color: #6a6c6f;
}

.rfm-sem-limite {
  color: #6a6c6f;
  font-style: italic;
}

.rfm-input-faixa {
  max-width: 150px;
  margin-top: 20px;
}

/* Sem validação nesta tabela: o slot de mensagens só deixaria as linhas altas. */
::v-deep .rfm-input-faixa .v-text-field__details {
  display: none;
}

::v-deep .rfm-input-faixa .v-input__slot {
  margin-bottom: 0;
}
</style>
